import { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { VIDEOS } from '@/content/site';

const ease = [0.16, 1, 0.3, 1];

function GalleryVideo({ item, index }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);

  const togglePlay = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <motion.figure
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.85, ease, delay: index * 0.08 }}
      className="luxe-hover-lift group relative rounded-[1.25rem] overflow-hidden border border-[hsl(var(--hairline))] bg-[hsl(var(--secondary))] luxe-shadow"
      data-testid={`gallery-item-${item.id}`}
    >
      <div className="relative aspect-[9/16] sm:aspect-[4/5] overflow-hidden">
        <video
          ref={ref}
          src={item.src}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={item.poster}
          className="w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05]"
          data-testid={`gallery-video-${item.id}`}
        />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[hsl(var(--warm-ink))]/40 to-transparent pointer-events-none" />

        {/* Controls overlay */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-3 right-3 flex items-center gap-2 pointer-events-auto opacity-0 group-hover:opacity-100 transition-opacity duration-500">
            <button
              type="button"
              onClick={togglePlay}
              data-testid={`gallery-play-${item.id}`}
              aria-label={playing ? 'Pause video' : 'Play video'}
              className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[hsl(var(--cream))]/95 backdrop-blur text-[hsl(var(--warm-ink))] hover:bg-[hsl(var(--cream))] transition-colors"
            >
              {playing ? <Pause className="w-3.5 h-3.5" strokeWidth={1.25} /> : <Play className="w-3.5 h-3.5" strokeWidth={1.25} />}
            </button>
            <button
              type="button"
              onClick={toggleMute}
              data-testid={`gallery-mute-${item.id}`}
              aria-label={muted ? 'Unmute video' : 'Mute video'}
              className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[hsl(var(--cream))]/95 backdrop-blur text-[hsl(var(--warm-ink))] hover:bg-[hsl(var(--cream))] transition-colors"
            >
              {muted ? <VolumeX className="w-3.5 h-3.5" strokeWidth={1.25} /> : <Volume2 className="w-3.5 h-3.5" strokeWidth={1.25} />}
            </button>
          </div>

          <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--cream))]/90 backdrop-blur px-3 py-1 text-[0.58rem] tracking-[0.32em] uppercase font-light">
            Reel · 0{index + 1}
          </div>
        </div>
      </div>

      <figcaption className="px-6 py-6">
        <div className="font-display text-[1.1rem] lg:text-[1.25rem] tracking-tight">
          {item.title}
        </div>
        <div className="mt-1.5 text-[0.78rem] text-[hsl(var(--muted-foreground))] leading-relaxed">
          {item.caption}
        </div>
      </figcaption>
    </motion.figure>
  );
}

export default function VideoGallery() {
  return (
    <section id="gallery" className="py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16 lg:mb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, ease }}
          >
            <div className="text-overline mb-7">The Gallery</div>
            <h2 className="font-display text-[1.75rem] sm:text-3xl lg:text-[2.5rem] leading-[1.15] max-w-2xl">
              Lash lifts in
              <span className="italic text-[hsl(var(--primary))] font-medium"> motion.</span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, ease, delay: 0.08 }}
            className="max-w-md text-[hsl(var(--muted-foreground))] leading-relaxed"
          >
            Real client moments from our Burrard Street studio. Hover any reel to pause or
            unmute — every video plays natively on the site.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-7 lg:gap-9">
          {VIDEOS.gallery.map((item, i) => (
            <GalleryVideo key={item.id} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
